import React, { useState, useEffect } from 'react';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import { ApplicationView, useController } from '@aha-app/mvc';
import ToolButton from '../toolbars/ToolButton';
import classNames from 'classnames';
import startArrowNoneSVG from '../assets/icons/startArrowNone.svg';
import endArrowNoneSVG from '../assets/icons/endArrowNone.svg';
import startArrowOpenSVG from '../assets/icons/startArrowOpen.svg';
import endArrowOpenSVG from '../assets/icons/endArrowOpen.svg';
import startArrowFilledSVG from '../assets/icons/startArrowFilled.svg';
import endArrowFilledSVG from '../assets/icons/endArrowFilled.svg';
import startArrowSVG from '../assets/icons/startArrow.svg';
import endArrowSVG from '../assets/icons/endArrow.svg';
import startCircleFilledSVG from '../assets/icons/startCircleFilled.svg';
import endCircleFilledSVG from '../assets/icons/endCircleFilled.svg';
import startCircleSVG from '../assets/icons/startCircle.svg';
import endCircleSVG from '../assets/icons/endCircle.svg';
import startDiamondSVG from '../assets/icons/startDiamond.svg';
import endDiamondSVG from '../assets/icons/endDiamond.svg';
import startSlashSVG from '../assets/icons/startSlash.svg';
import endSlashSVG from '../assets/icons/endSlash.svg';
import { svgToIcon } from '../utils/svg';
import styles from '../styles.module.css';
import { ConnectorEnd } from '../types';
import WhiteboardController from '../controllers/WhiteboardController';
import Tool, { type ToolButtonComponent } from './Tool';
import { type ConnectorEndStyle, CONNECTOR_END_STYLE } from '../constants';

const canUseAdvancedConnectors = window.currentAccount?.whiteboardsAdvanced;

const allEndStyles: {
  endStyle: ConnectorEndStyle;
  startIcon: string;
  endIcon: string;
  isAdvanced?: boolean;
}[] = [
  {
    endStyle: CONNECTOR_END_STYLE.NONE,
    startIcon: startArrowNoneSVG,
    endIcon: endArrowNoneSVG,
  },
  {
    endStyle: CONNECTOR_END_STYLE.POINTY_ARROW,
    startIcon: startArrowOpenSVG,
    endIcon: endArrowOpenSVG,
  },
  {
    endStyle: CONNECTOR_END_STYLE.FILLED_ARROW,
    startIcon: startArrowFilledSVG,
    endIcon: endArrowFilledSVG,
  },
  {
    endStyle: CONNECTOR_END_STYLE.ARROW,
    startIcon: startArrowSVG,
    endIcon: endArrowSVG,
    isAdvanced: true,
  },
  {
    endStyle: CONNECTOR_END_STYLE.FILLED_CIRCLE,
    startIcon: startCircleFilledSVG,
    endIcon: endCircleFilledSVG,
  },
  {
    endStyle: CONNECTOR_END_STYLE.CIRCLE,
    startIcon: startCircleSVG,
    endIcon: endCircleSVG,
    isAdvanced: true,
  },
  {
    endStyle: CONNECTOR_END_STYLE.DIAMOND,
    startIcon: startDiamondSVG,
    endIcon: endDiamondSVG,
    isAdvanced: true,
  },
  {
    endStyle: CONNECTOR_END_STYLE.SLASH,
    startIcon: startSlashSVG,
    endIcon: endSlashSVG,
    isAdvanced: true,
  },
] as const;

const permittedEndStyles = allEndStyles.filter(
  item => !item.isAdvanced || (item.isAdvanced && canUseAdvancedConnectors)
);

function iconForEndStyle(
  endStyleInfo: (typeof allEndStyles)[number],
  lineEnd: ConnectorEnd
) {
  switch (lineEnd) {
    case ConnectorEnd.CONNECTOR_START:
      return svgToIcon(endStyleInfo.startIcon);
    case ConnectorEnd.CONNECTOR_END:
      return svgToIcon(endStyleInfo.endIcon);
    default: {
      const _exhaustivenessCheck: never = lineEnd;
      return _exhaustivenessCheck;
    }
  }
}

interface PopupComponentProps {
  currentLineEndStyle: number;
  setCurrentLineEndStyle: (number) => void;
  selection: fabric.Object;
  lineEnd: number;
}

const PopupComponent: React.FC<PopupComponentProps> = ({
  currentLineEndStyle,
  setCurrentLineEndStyle,
  selection,
  lineEnd,
}) => {
  const controller = useController(WhiteboardController);

  const setEndStyle = (endStyle: ConnectorEndStyle) => {
    setCurrentLineEndStyle(endStyle);
    const newStyles = selection.firstObject().get('endStyles').slice(0);
    newStyles[lineEnd] = endStyle;
    selection.setOnObjects('endStyles', newStyles);
    Tool.toolActionComplete({ controller });
  };

  return (
    <ToolbarMenu columns={1}>
      {permittedEndStyles.map((styleInfo, i) => (
        <div
          key={i}
          className={classNames(styles.toolButton, {
            [styles.toolButtonActive]:
              currentLineEndStyle === styleInfo.endStyle,
          })}
          onClick={() => setEndStyle(styleInfo.endStyle)}
        >
          {iconForEndStyle(styleInfo, lineEnd)}
        </div>
      ))}
    </ToolbarMenu>
  );
};

const Popup = ApplicationView(PopupComponent);

const ConnectorArrowButton = (lineEnd: ConnectorEnd) => {
  const ConnectorArrowButtonComponent: ToolButtonComponent = ({
    selection,
    tool,
  }) => {
    const lineEndStyle = selection.firstObject().get('endStyles')[
      lineEnd
    ] as ConnectorEndStyle;
    const [currentLineEndStyle, setCurrentLineEndStyle] =
      useState(lineEndStyle);

    // Update color if the props change.
    useEffect(() => {
      setCurrentLineEndStyle(lineEndStyle);
    }, [lineEndStyle]);

    const endStyleInfo = allEndStyles.find(
      es => es.endStyle === currentLineEndStyle
    );
    if (!endStyleInfo)
      throw new Error(`Unknown end style ${currentLineEndStyle}`);

    return (
      <ToolButton
        popup={
          <Popup
            currentLineEndStyle={currentLineEndStyle}
            setCurrentLineEndStyle={setCurrentLineEndStyle}
            selection={selection}
            lineEnd={lineEnd}
          />
        }
        className='connectorEnd'
        tool={tool}
      >
        {iconForEndStyle(endStyleInfo, lineEnd)}
      </ToolButton>
    );
  };

  return ApplicationView(ConnectorArrowButtonComponent);
};

export default ConnectorArrowButton;
